import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router";
import { CherryPayApi } from "../api/models";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator/LoadingIndicator";
import { ApiRequestState, useApiRequest } from "../hooks/useApiRequest";
import { LicencedProduct } from "../enums/LicencedProduct";

interface TModelContext {
  business: ApiRequestState<CherryPayApi.BusinessSummary | null>;
  roles: ApiRequestState<CherryPayApi.CherryHubRolesSummary>;
}

const ModelContext = createContext<TModelContext | null>(null);

/**
 * Loads models from the api based on the presence of react-router path parameters. Example: if
 * the `businessId` path parameter is set, this component will display loading & error states and
 * then inject the business into the context once loaded.
 */
export const ModelContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { businessId } = useParams();

  const businessRequest = useApiRequest(
    (apiClient) => {
      if (!businessId) {
        return null;
      }
      return apiClient.getBusiness(businessId);
    },
    [businessId]
  );

  const rolesRequest = useApiRequest(
    (apiClient) => {
      if (!businessId) {
        return null;
      }

      if (businessRequest.isLoading) {
        return null;
      }

      // we will get only CherryPay roles for now. When we have more than one product,
      // we might build a bulk endpoint to get all roles for a business.
      return apiClient.getMyIamRolesForProduct(
        businessId,
        LicencedProduct.CherryPay
      );
    },
    [businessId, businessRequest.isLoading]
  );

  const { isLoading, error, notFound } = useMemo(
    () => ({
      isLoading:
        (!!businessId && businessRequest.isLoading) ||
        (!!businessId && rolesRequest.isLoading),
      error:
        (!!businessId ? businessRequest.error : null) ||
        (!!businessId ? rolesRequest.error : null),
      notFound:
        (!!businessId && !businessRequest.isLoading && !businessRequest.data) ||
        (!!businessId && !rolesRequest.isLoading && !rolesRequest.data),
    }),
    [businessId, businessRequest, rolesRequest]
  );

  const context = useMemo(
    () => ({
      business: businessRequest,
      roles: rolesRequest,
    }),
    [businessRequest, rolesRequest]
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (error || notFound) {
    return (
      <ErrorMessage>
        An error was encountered while loading the page
      </ErrorMessage>
    );
  }

  return (
    <ModelContext.Provider value={context}>{children}</ModelContext.Provider>
  );
};

/**
 * Returns the business loaded from the `:businessId` path parameter
 */
export const useBusinessContext = (): CherryPayApi.BusinessSummary => {
  const ctx = useContext(ModelContext);

  if (!ctx || !ctx.business.data) {
    throw new Error("Missing business context");
  }

  return ctx.business.data;
};

export const useRolesContext = (): CherryPayApi.CherryHubRolesSummary => {
  const ctx = useContext(ModelContext);

  if (!ctx || !ctx.roles.data) {
    throw new Error("Missing roles context");
  }

  return ctx.roles.data;
};

/**
 * Non-throwing variant of {@link useRolesContext}. Returns `null` when the
 * roles context is not available (e.g. when called from a provider that sits
 * above `ModelContextProvider`, or on routes without a `:businessId`).
 */
export const useOptionalRolesContext =
  (): CherryPayApi.CherryHubRolesSummary | null => {
    const ctx = useContext(ModelContext);
    return ctx?.roles.data ?? null;
  };

/** @see MemberContext — member is loaded on the member detail route, not here. */
export { useMemberContext } from "./MemberContext";
